<template>
  <div class="reward">
    <div class="reward__header text-center" :class="headerClass">
      <slot name="icon">
        <svg-icon v-if="icon" :icon="icon" class="icon-lg mr-1" />
      </slot>
      <span class="d-inline-block">{{ title }}</span>
    </div>
    <div class="body txt-6" :class="{ 'is-disabled': disabled }">
      <slot>
        <textarea
          class="mb-4 txt-5-alt"
          :class="{ 'form-item-error': errors.description }"
          data-cy="reward-description"
          :placeholder="
            errors.description ? errors.description : $t('description')
          "
          v-model="edit.description"
          :disabled="!isEdit || disabled"
          v-if="
            (value.description !== '' || value.description !== null) && isEdit
          "
          @input="autosize($event)"
          @focus="autosize($event)"
        ></textarea>
        <p
          v-if="
            !isEdit && (value.description !== '' || value.description !== null)
          "
        >
          {{ value.description }}
        </p>
        <template v-if="isEdit">
          <div>
            <b-form-row v-if="!disabledLimits">
              <b-col v-for="(limit, index) in edit.limits" :key="index">
                <b-form-group class="small" :class="{ 'mb-0': !metricTitle }">
                  <legend class="col-form-label pt-0 pb-0 text-uppercase">
                    {{ $t(limitTypes[limit.type]) }} {{ $t('limit') }}
                  </legend>
                  <div class="d-flex">
                    <b-input-number
                      size="sm"
                      :placeholder="$t('min').toUpperCase()"
                      type="text"
                      :disabled="disabled"
                      v-model.number="edit.limits[index].min"
                      class="p-0 text-5-alt border-top-0 border-left-0 border-right-0 rounded-0 shadow-none"
                    />
                    <div class="ml-1 mr-1 text-lighten">-</div>
                    <b-input-number
                      size="sm"
                      :placeholder="$t('max').toUpperCase()"
                      :disabled="disabled"
                      v-model.number="edit.limits[index].max"
                      type="text"
                      class="p-0 text-5-alt border-top-0 border-left-0 border-right-0 rounded-0 shadow-none"
                    />
                  </div>
                </b-form-group>
              </b-col>
            </b-form-row>
            <template v-if="metricTitle">
              <b-form-row
                v-for="(item, index) in edit.slots"
                :key="index"
                data-cy="subtask-slot"
              >
                <b-col>
                  <b-form-group class="small">
                    <legend class="col-form-label pt-0 pb-0">
                      {{ metricTitle | upperCase }}
                    </legend>
                    <div class="d-flex">
                      <b-form-group
                        :class="{
                          'small mb-0': true,
                          'form-item-error':
                            errors[`slots[${index}]`] &&
                            errors[`slots[${index}]`].min
                        }"
                        :title="
                          errors[`slots[${index}]`] &&
                            errors[`slots[${index}]`].min
                        "
                        v-b-tooltip.hover
                      >
                        <b-input-number
                          :data-cy="`slot-${index}-min`"
                          size="sm"
                          :placeholder="$t('min').toUpperCase()"
                          type="number"
                          :disabled="disabled"
                          v-model.number="item.min"
                          class="p-0 text-5-alt border-top-0 border-left-0 border-right-0 rounded-0 shadow-none"
                        />
                      </b-form-group>
                      <div class="ml-1 mr-1 text-lighten">-</div>
                      <b-form-group
                        :class="{
                          'small mb-0': true,
                          'form-item-error':
                            errors[`slots[${index}]`] &&
                            errors[`slots[${index}]`].max
                        }"
                        :title="
                          errors[`slots[${index}]`] &&
                            errors[`slots[${index}]`].max
                        "
                        v-b-tooltip.hover
                      >
                        <b-input-number
                          :data-cy="`slot-${index}-max`"
                          size="sm"
                          :placeholder="$t('max').toUpperCase()"
                          :disabled="disabled"
                          v-model.number="item.max"
                          type="text"
                          class="p-0 text-5-alt border-top-0 border-left-0 border-right-0 rounded-0 shadow-none"
                        />
                      </b-form-group>
                    </div>
                  </b-form-group>
                </b-col>
                <b-col>
                  <div class="d-flex">
                    <b-form-group
                      :data-cy="`slot-${index}-amount-group`"
                      class="small"
                      :class="{
                        'form-item-error':
                          errors[`slots[${index}]`] &&
                          errors[`slots[${index}]`].reward &&
                          errors[`slots[${index}]`].reward.amount
                      }"
                      v-b-tooltip.hover
                      :title="
                        errors[`slots[${index}]`] &&
                          errors[`slots[${index}]`].reward &&
                          errors[`slots[${index}]`].reward.amount
                      "
                    >
                      <legend class="col-form-label pt-0 pb-0">
                        {{ $t('amount') | upperCase }}
                      </legend>
                      <b-input-number
                        :data-cy="`slot-${index}-amount`"
                        size="sm"
                        v-model.number="item.reward.amount"
                        :disabled="disabled"
                        @input="handleEmptyString($event, index)"
                        class="p-0 text-5-alt border-top-0 border-left-0 border-right-0 rounded-0 shadow-none"
                        type="text"
                      />
                    </b-form-group>
                    <button
                      v-if="index > 0"
                      @click="removeSlot(index)"
                      class="border-0 btn-link-alt ml-2"
                      v-b-tooltip.hover
                      :title="$t('removeSlot')"
                    >
                      <svg-icon icon="cross" class="icon-xs" />
                    </button>
                  </div>
                </b-col>
              </b-form-row>
            </template>
            <b-form-row v-else>
              <b-col>
                <b-form-group
                  class="small mb-0"
                  v-for="(item, index) in slot.prices"
                  :key="index"
                >
                  <legend class="col-form-label pt-0 pb-0">
                    {{ $t('amount') | upperCase }}
                  </legend>
                  <b-form-input
                    size="sm"
                    :disabled="disabled"
                    v-model="item.reward.amount"
                    class="p-0 text-5-alt border-top-0 border-left-0 border-right-0 rounded-0 shadow-none"
                    type="text"
                  />
                </b-form-group>
              </b-col>
            </b-form-row>
            <slot name="bottom"></slot>
          </div>
          <button
            v-if="metricTitle && !disabled"
            data-cy="add-slot"
            class="add-slot"
            @click="addSlot()"
          >
            <svg-icon icon="plus" class="icon-xs mr-1 align-middle" />
            <span class="align-middle small font-weight-bold">
              {{ $t('addSlot') | upperCase }}
            </span>
          </button>
        </template>
        <div v-else>
          <b-form-row v-if="!disabledLimits">
            <b-col v-for="(limit, index) in value.limits" :key="index">
              <b-form-group :class="'mb-0'">
                <legend class="col-form-label pt-0 pb-0 text-uppercase">
                  {{ $t(`${limitTypes[limit.type]}Limit`) }}
                </legend>
                <div class="d-flex">
                  {{ value.limits[index].min }}
                  <div class="ml-1 mr-1 text-lighten">-</div>
                  {{ value.limits[index].max }}
                </div>
              </b-form-group>
            </b-col>
          </b-form-row>
          <div v-for="(item, index) in value.slots" :key="index">
            <span v-if="metricTitle">
              {{ item.min }} - {{ item.max }} {{ metricTitle }} -
              <strong>
                {{ item.reward.amount }} {{ item.reward.currency.code }}
              </strong>
            </span>
            <strong v-else>
              {{ item.reward.amount }} {{ item.reward.currency.code }}
            </strong>
          </div>
        </div>
      </slot>
      <slot name="bottom"></slot>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'clone';
import isEqual from 'js-isequal';

export default {
  props: {
    isEdit: {
      type: Boolean,
      default: false
    },
    icon: {
      validator(value) {
        return (
          value === null || typeof value === 'string' || value instanceof String
        );
      },
      default: null
    },
    headerClass: {
      type: String,
      default: 'twitter-color'
    },
    title: {
      type: String,
      default: ''
    },
    limits: {
      type: Array,
      default() {
        return [];
      }
    },
    prices: {
      type: Array,
      default() {
        return [];
      }
    },
    metricTitle: {
      type: String,
      default: ''
    },
    disabledLimits: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Object,
      default() {
        return {};
      }
    },
    value: {
      type: Object,
      default: () => ({})
    },
    name: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      limitTypes: {
        '1': 'daily',
        '7': 'weekly'
      },
      edit: cloneDeep(this.value)
    };
  },

  methods: {
    autosize(e) {
      const el = e.target;
      el.style.cssText = 'height: auto; padding: 0;';
      el.style.cssText = 'height:' + el.scrollHeight + 'px';
    },

    addSlot() {
      this.edit.slots.push({ min: null, max: null, reward: { amount: null } });
    },

    removeSlot(index) {
      this.edit.slots.splice(index, 1);
    },

    handleEmptyString(val, i) {
      if (val === '') this.edit.slots[i].reward.amount = null;
    }
  },

  watch: {
    value: {
      handler() {
        this.edit = cloneDeep(this.value);
      }
    },
    edit: {
      deep: true,
      handler(val) {
        if (this.name === '') {
          throw new Error('you must pass "name" prop for editing reward');
        }
        if (!isEqual(val, this.value)) {
          this.$emit('input', { name: this.name, data: cloneDeep(val) });
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.reward {
  border: 1px solid $border-color;
  border-radius: $border-radius;

  &__header {
    border-bottom: 1px solid $border-color;
    padding: 1rem;

    span {
      font-size: 18px;
      vertical-align: middle;
    }
  }

  .body {
    &.is-disabled {
      background: $disabled-color;

      textarea {
        padding: 5px 10px;
      }
    }
  }
}

.body {
  padding: 1rem;
}

textarea {
  display: block;
  border: none;
  border-bottom: 1px solid $border-color;
  resize: none;
  width: 100%;
  color: $body-color;
  height: auto;
  overflow: hidden;
  padding: 0;
  box-sizing: padding-box;
  height: 54px;

  &:focus {
    border-color: $secondary;
  }

  &.form-item-error {
    border-color: $danger;

    &::placeholder {
      color: $danger;
    }
  }

  &:disabled {
    background-color: $disabled-color;
  }
}

.add-slot {
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  color: $primary;
  cursor: pointer;

  &:hover {
    color: $primary-hover;
  }

  &:focus,
  &:active {
    color: $primary-active;
  }
}

.form-item-error {
  legend {
    color: $danger;
  }

  input {
    border-color: $danger;
  }
}
</style>
